"use client"

import { useEffect, useRef } from "react"

import type { UnlikelyProduct } from "@unlikelystudio/commerce-connector"
import { useGetCustomer } from "@unlikelystudio/react-ecommerce-hooks"

import { useEmitCustomAnalyticsEvent } from "~/hooks/tracking/useEmitCustomAnalyticsEvent"
import useLocale from "~/hooks/useLocale"
import type { TVariant } from "~/components/ui/ProductHeader/_data/types"
import { unlikelyProductToTrackingData } from "~/components/shared/AnalyticsEvents/utils/unlikely-product-to-tracking-data"
import { variantToTrackingData } from "~/components/shared/AnalyticsEvents/utils/variant-to-tracking-data"
import { uniqBy } from "~/utils/uniq"

type ClientViewItemProps = {
  product: UnlikelyProduct
  activeVariant: TVariant | null
}

export function ClientViewItem({ activeVariant, product }: ClientViewItemProps) {
  const { emitCustomEvent } = useEmitCustomAnalyticsEvent()
  const locale = useLocale()
  const { data: customer, isInitialLoading } = useGetCustomer()
  const payload = unlikelyProductToTrackingData({ product, locale })

  // we are storing the variants in case the active variants changes before the customer is fetched
  const variantsQueue = useRef<(TVariant | null)[]>([])

  useEffect(() => {
    // if the customer is not fetched yet, we store the variants in the queue
    if (isInitialLoading) {
      variantsQueue.current.push(activeVariant)
      return
    }

    // if the active variant is not in the queue, we emit the event
    if (!variantsQueue.current.find((variant) => variant?.id === activeVariant?.id)) {
      emitCustomEvent<"view_item">("view_item", {
        items: [{ ...payload, ...variantToTrackingData(activeVariant, customer) }],
      })
    }

    // we filter the queue to avoid duplicate variants
    const uniqueVariants = uniqBy(variantsQueue.current.filter(Boolean), "id")

    // we emit the events for the unique variants that have been added to the queue
    uniqueVariants.forEach((variant) => {
      emitCustomEvent<"view_item">("view_item", {
        items: [{ ...payload, ...variantToTrackingData(variant, customer) }],
      })
    })
    variantsQueue.current = []
  }, [activeVariant, isInitialLoading])

  return null
}
